import { ScheduledConsultationTypeDTO, type PatientDTO } from '@/generated'
import type { Patient } from '@/util/consultationPatient'
import { translateGenderDTO } from '@/util/translations'
import { differenceInYears, parseISO } from 'date-fns'
import { useI18n } from 'vue-i18n'
import { useFormatDate } from '@/composables/useFormatDate'

const CONFERENCE_PATIENT_LIMIT = 4
const PATIENT_PRESENTATION_PATIENT_LIMIT = 2
const WARD_ROUND_PATIENT_LIMIT = 10

export const useConsultationPatient = () => {
  const { t } = useI18n()
  const { formatDateOnly } = useFormatDate()

  function formatPatientFullname(patient: Patient): string {
    const firstName = patient.firstName ? `, ${patient.firstName}` : ''

    return `${patient.lastName}${firstName}`
  }

  function formatPatientDetails(patient: Patient): string {
    const dateOfBirth = patient.dateOfBirth ? formatDateOnly(patient.dateOfBirth) : ''
    const departmentName = patient.department ? `, ${t(patient.department.name)}` : ''
    const hospital = patient.hospital.name ? `, ${patient.hospital.name}` : ''
    return `${translateGenderDTO(patient.gender, t)}, ${t('b.')} ${dateOfBirth}${departmentName}${hospital}, ${patient.externalId}`
  }

  function formatPatientCompactDetails(patient: Patient): string {
    const dateOfBirth = patient.dateOfBirth
      ? `${differenceInYears(new Date(), patient.dateOfBirth)}, `
      : ''
    return `${translateGenderDTO(patient.gender, t)}, ${dateOfBirth}${patient.externalId}`
  }

  function formatPatientDisplay(patient: Patient): string {
    return `${formatPatientFullname(patient)} (${formatPatientDetails(patient)})`
  }

  function formatPatientBasicDetails(patient: Pick<Patient, 'gender' | 'dateOfBirth'>): string {
    if (!patient.dateOfBirth) {
      return `${translateGenderDTO(patient.gender, t)}`
    }
    return `${translateGenderDTO(patient.gender, t)}, ${differenceInYears(new Date(), patient.dateOfBirth)}`
  }

  function formatPatientDTODetails(patient: PatientDTO): string {
    return formatPatientBasicDetails({
      gender: patient.gender,
      dateOfBirth: patient.dateOfBirth ? parseISO(patient.dateOfBirth) : undefined
    })
  }

  function getPatientLimitByConsultationType(
    consultationType: ScheduledConsultationTypeDTO
  ): number {
    switch (consultationType) {
      case ScheduledConsultationTypeDTO.CONFERENCE:
        return CONFERENCE_PATIENT_LIMIT
      case ScheduledConsultationTypeDTO.PATIENT_PRESENTATION:
        return PATIENT_PRESENTATION_PATIENT_LIMIT
      case ScheduledConsultationTypeDTO.PATIENT_VISIT:
      default:
        return WARD_ROUND_PATIENT_LIMIT
    }
  }

  return {
    formatPatientFullname,
    formatPatientDetails,
    formatPatientBasicDetails,
    formatPatientCompactDetails,
    formatPatientDisplay,
    formatPatientDTODetails,
    getPatientLimitByConsultationType
  }
}
